import { Dayjs } from 'dayjs';
import { Role, Visitor } from 'graphql/generated/graphqlTypes';
import { VisitorGroupType } from 'utils/VisitorGroup/VisitorGroup';
import { VisitorGroupAutocompleteOption } from 'v2/utils/visitorGroupAutocomplete';

export interface UserFormValues {
  name: string;
  email: string;
  visitorGroup: VisitorGroupAutocompleteOption[];
  isAdministratorRole: boolean;
  isInstallerRole: boolean;
  expirationDate: Dayjs | null;
}

interface DefaultValuesParams {
  visitor?: Visitor;
  preSelectedGroup?: VisitorGroupAutocompleteOption | null;
  isAdministratorsGroup: boolean;
  isInstallersGroup: boolean;
}

export const getUserFormDefaultValues = ({
  visitor,
  preSelectedGroup,
  isAdministratorsGroup,
  isInstallersGroup
}: DefaultValuesParams): UserFormValues => ({
  name: visitor?.name ?? '',
  email: visitor?.email ?? '',
  visitorGroup: preSelectedGroup ? [preSelectedGroup] : [],
  isAdministratorRole: visitor?.isAdmin ?? isAdministratorsGroup,
  isInstallerRole:
    visitor?.visitorGroupMembership.some((m) => m.visitorGroup.type === VisitorGroupType.Installers) ??
    isInstallersGroup,
  expirationDate: null
});

/**
 * Maps the form values to the `addOrEditVisitor` arguments. An existing `visitor` means edit:
 * the memberships (including their per-group expiration dates) are preserved and name/email are not
 * changed through the group fields.
 */
export const getVisitorMutationInput = (data: UserFormValues, visitor?: Visitor) => {
  if (visitor) {
    return {
      isNewVisitor: false,
      name: data.name,
      email: data.email,
      id: visitor.id,
      visitorGroupMembershipInput: visitor.visitorGroupMembership.map((m) => ({
        role: m.role,
        visitorGroupId: m.visitorGroup.id,
        expirationDate: m.expirationDate
      })),
      shouldBeAdmin: data.isAdministratorRole,
      shouldBeInstaller: data.isInstallerRole,
      expirationDate: undefined
    };
  }

  return {
    isNewVisitor: true,
    name: data.name,
    email: data.email,
    id: undefined,
    visitorGroupMembershipInput: data.visitorGroup.map((group) => ({ visitorGroupId: group.value, role: Role.None })),
    shouldBeAdmin: data.isAdministratorRole,
    shouldBeInstaller: data.isInstallerRole,
    expirationDate: data.expirationDate ?? undefined
  };
};
